/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect } from 'react';
import mbpLogger from '@module-logger/mbp';
import { useDispatch, useSelector } from 'react-redux';
import {
    Drawer,
    SwipeableDrawer,
    IconButton,
    Box,
    useMediaQuery,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { func, string } from 'prop-types';

import PersonalizedOfferInbox from './PersonalizedOfferInbox';
import cookies from '../../../../../helpers/common/storage/cookies';
import { getOfferInboxData } from '../../../../../../state/ducks/App/App-Selectors';
import { setOfferInboxDrawerOpen } from '../../../../../../state/ducks/App/App-Actions';
import OfferInboxSkeleton from '../../../GraphqlSkeletonComponents/OfferInboxSkeleton';
import noop from '../../../../../helpers/noop';
import useExperienceInboxQuery from '../../../../../helpers/Personalization/useExperienceInboxQuery';
import ComponentErrorBoundary from '../../../EnterpriseID/Common/ComponentErrorBoundary/ComponentErrorBoundary';

const useStyles = makeStyles((theme) => ({
    drawerPaper: {
        padding: 0,
        backgroundColor: '#fff',
    },
    drawerBackdrop: {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
    loadingContent: {
        padding: '24px',
        width: '414px',
        [theme.breakpoints.down('xs')]: {
            padding: '16px',
            height: 'calc(90dvh - 72px)',
        },
    },
    bottomSheetPaper: {
        backgroundColor: '#fff',
        borderRadius: '16px 16px 0 0',
    },
    dragHandleWrap: {
        cursor: 'grab',
        '&:active': {
            cursor: 'grabbing',
        },
    },
    drawerHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 4,
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        height: '0',
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    closeBtn: {
        position: 'relative',
        top: '30px',
        right: '10px',
        width: '24px',
        height: '24px',
    },
}));

const OfferInboxContainer = ({ instanceId, handleClose }) => {
    const offerInbox = useSelector(getOfferInboxData);
    const dispatch = useDispatch();
    const classes = useStyles();
    const drawerOpen = offerInbox.isDrawerOpen?.[instanceId];

    const isMobile = useMediaQuery('(max-width:599.95px)', { noSsr: true });

    const handleCloseDrawer = () => {
        dispatch(setOfferInboxDrawerOpen(null));
        handleClose();
    };

    if (isMobile && drawerOpen) {
        return (
            <SwipeableDrawer
                anchor="bottom"
                open={!!drawerOpen}
                onClose={handleCloseDrawer}
                onOpen={noop}
                disableSwipeToOpen
                keepMounted={false}
                classes={{
                    paper: classes.bottomSheetPaper,
                }}
                BackdropProps={{
                    classes: { root: classes.drawerBackdrop },
                }}
            >
                <Box className={classes.dragHandleWrap} aria-hidden="true" />
                <Box id="offer-inbox-drawer-content">
                    <OfferInboxQuery handleCloseDrawer={handleCloseDrawer} />
                </Box>

            </SwipeableDrawer>
        );
    }

    if (drawerOpen) {
        return (
            <Drawer
                anchor="right"
                id="offer-inbox-drawer"
                open={drawerOpen}
                onClose={handleCloseDrawer}
                classes={{
                    paper: classes.drawerPaper,
                }}
                BackdropProps={{
                    classes: { root: classes.drawerBackdrop },
                }}
                SlideProps={{
                    direction: 'left',
                }}
            >
                <OfferInboxQuery handleCloseDrawer={handleCloseDrawer} />
            </Drawer>
        );
    }

    return null;
};

OfferInboxContainer.propTypes = {
    instanceId: string.isRequired,
    handleClose: func,
};

OfferInboxContainer.defaultProps = {
    handleClose: noop,
};

const OfferInboxQuery = ({ handleCloseDrawer }) => {
    const persistedUserProfile = cookies.get('persisted_user_profile');
    const classes = useStyles();
    const {
        loading, error, data, variables,
    } = useExperienceInboxQuery({
        queryName: 'OfferInbox',
        resolveIdentity: true,
        isSessionHasAccount: !!persistedUserProfile,
    });

    const skip = variables?.skip;
    const hasOffers = data?.experienceOfferInbox?.availableOffers?.length > 0;

    useEffect(() => {
        if (error) {
            mbpLogger.logError({
                function: 'OfferInboxContainer',
                message: 'Error loading API data from OfferInbox',
                appName: process.env.npm_package_name,
                module: 'Personalized Offer Inbox',
                jsError: error,
            });
            handleCloseDrawer();
            return;
        }

        if (!loading && !skip && !hasOffers) {
            handleCloseDrawer();
        }
    }, [error, loading, skip, hasOffers, handleCloseDrawer]);

    if (loading || skip) {
        return (
            <Box className={classes.loadingContent} id="offer-inbox-drawer-content">
                <OfferInboxSkeleton />
            </Box>
        );
    }

    if (hasOffers) {
        const bannerData = data.experienceOfferInbox.content || {};
        return (
            <ComponentErrorBoundary function="OfferInboxContainer" module="PersonalizedContent" message="something went wrong in personalized offer inbox">
                <Box className={classes.drawerHeader} aria-hidden="false">
                    <div
                        className={classes.closeBtn}
                        onClick={handleCloseDrawer}
                        onKeyDown={handleCloseDrawer}
                        role="button"
                        tabIndex={0}
                    >
                        <img src={bannerData?.close_button?.icon?.url} alt="close" />
                    </div>
                </Box>

                <PersonalizedOfferInbox data={data.experienceOfferInbox} handleCloseDrawer={handleCloseDrawer} />
            </ComponentErrorBoundary>
        );
    }

    return null;
};

OfferInboxQuery.propTypes = {
    handleCloseDrawer: func,
};

OfferInboxQuery.defaultProps = {
    handleCloseDrawer: noop,
};

export default OfferInboxContainer;
